import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import * as yup from "yup";

import {
  useConfirmEmailMutation,
  useSendConfirmEmailMutation,
  useConfirmSignUpQuery,
  useConfirmAccountMutation,
} from "../../app/api/public/account";
import { HttpStatus } from "../../app/constants/HttpStatus";
import { Account, AccountStatus } from "../../app/models/Account";
import { router } from "../../app/services/router";
import { tracking } from "../../app/services/tracking";
import { Button } from "../shared/Button";
import { FieldController } from "../shared/FieldController";
import { Form } from "../shared/Form";
import { Markdown } from "../shared/Markdown";
import { Separator } from "../shared/Separator";
import { TextField } from "../shared/TextField";
import { AccountForm } from "./AccountForm";
import { AccountSendActivationEmailForm } from "./AccountSendActivationEmailForm";

export type SignUpState =
  | ConfirmAccountStepState
  | ConfirmEmailStepState
  | ConfirmSignUpStepState
  | GetAccountWithCharacterCodeStepState
  | GetAccountWithEmailStepState
  | ResultStepState;

export enum SignUpStateType {
  CONFIRM_ACCOUNT = "CONFIRM_ACCOUNT",
  CONFIRM_EMAIL = "CONFIRM_EMAIL",
  CONFIRM_SIGN_UP = "CONFIRM_SIGN_UP",
  GET_ACCOUNT_WITH_CHARACTER_CODE = "GET_ACCOUNT_WITH_CHARACTER_CODE",
  GET_ACCOUNT_WITH_EMAIL = "GET_ACCOUNT_WITH_EMAIL",
  RESULT = "RESULT",
}

type AccountSignUpFormProps = {
  state: SignUpState;
  onChange: (state: SignUpState) => void;
};

export const AccountSignUpForm = (props: AccountSignUpFormProps) => {
  const { state, onChange } = props;

  if (state.step === SignUpStateType.CONFIRM_ACCOUNT) {
    return <ConfirmAccountStep account={state.account} onChange={onChange} />;
  }

  if (state.step === SignUpStateType.CONFIRM_EMAIL) {
    return (
      <ConfirmEmailStep
        code={state.code}
        type={state.type}
        onChange={onChange}
      />
    );
  }

  if (state.step === SignUpStateType.CONFIRM_SIGN_UP) {
    return <ConfirmSignUpStep session={state.session} onChange={onChange} />;
  }

  if (state.step === SignUpStateType.GET_ACCOUNT_WITH_CHARACTER_CODE) {
    return (
      <>
        <GetAccountWithCharacterCodeStep length={14} onChange={onChange} />
        <Separator variant="or" />
        <GetAccountWithCharacterCodeStep length={6} onChange={onChange} />
      </>
    );
  }

  if (state.step === SignUpStateType.GET_ACCOUNT_WITH_EMAIL) {
    return <GetAccountWithEmailStep onChange={onChange} />;
  }

  if (state.step === SignUpStateType.RESULT) {
    return (
      <ResultStep session={state.session} onChange={onChange}>
        {state.message}
      </ResultStep>
    );
  }

  return null;
};

type AccountSignUpFormMarkdownProps = {
  children: React.ReactNode;
  session?: { email?: string; username?: string };
  onChange: (state: SignUpState) => void;
};

const AccountSignUpFormMarkdown = (props: AccountSignUpFormMarkdownProps) => {
  const { children, session, onChange } = props;
  return (
    <Markdown
      options={{
        overrides: {
          AccountSendActivationEmailForm: {
            component: AccountSendActivationEmailForm,
            props: { value: session },
          },
          ForgotPasswordLink: {
            component: Link,
            props: { to: router.getTo("authenticationForgotPassword") },
          },
          GetAccountWithCharacterCodeLink: {
            component: "button",
            props: {
              type: "button",
              onClick: () => {
                onChange({
                  step: SignUpStateType.GET_ACCOUNT_WITH_CHARACTER_CODE,
                });

                tracking.trackEvent({
                  eventAction: "clic",
                  eventCategory: "pre-home",
                  eventLabel: "parcours_alternatif",
                });
              },
            },
          },
          SignInLink: { component: Link, props: { to: router.getTo("home") } },
        },
      }}
    >
      {children}
    </Markdown>
  );
};

type AccountSignUpFormMessageProps = {
  onChange: (state: SignUpState) => void;
};

const AccountSignUpFormMessage = (props: AccountSignUpFormMessageProps) => {
  const { onChange } = props;

  const { t } = useTranslation();
  const { action, text } = t(
    "features.account.AccountSignUpForm.message",
  ) as unknown as {
    action: { text: string };
    text: string;
  };

  return (
    <>
      <Separator variant="or" />
      <Button
        dataLayer={{
          eventAction: "clic",
          eventCategory: "pre-home",
          eventLabel: "parcours_alternatif",
        }}
        fluid
        type="button"
        variant="action"
        onClick={() =>
          onChange({
            step: SignUpStateType.GET_ACCOUNT_WITH_CHARACTER_CODE,
          })
        }
      >
        {action.text}
      </Button>
      <p>{text}</p>
    </>
  );
};

type ConfirmAccountStepState = {
  step: SignUpStateType.CONFIRM_ACCOUNT;
  account: Account;
};

type ConfirmAccountStepProps = {
  account: Account;
  onChange: (state: SignUpState) => void;
};

const ConfirmAccountStep = (props: ConfirmAccountStepProps) => {
  const { account, onChange } = props;

  const [confirmAccount] = useConfirmAccountMutation();

  const { t } = useTranslation();
  const { successMessage } = t(
    "features.account.AccountSignUpForm.steps.confirmAccount",
  ) as unknown as { successMessage: string };

  return (
    <AccountForm
      value={account}
      onSubmit={async (values) => {
        await confirmAccount(values).unwrap();

        onChange({
          step: SignUpStateType.RESULT,
          message: successMessage,
          session: { email: account.email },
        });
      }}
    />
  );
};

type ConfirmEmailStepState = {
  step: SignUpStateType.CONFIRM_EMAIL;
  code: string;
  type: "code" | "email";
};

type ConfirmEmailStepProps = {
  code: string;
  type: "code" | "email";
  onChange: (state: SignUpState) => void;
};

const ConfirmEmailStep = (props: ConfirmEmailStepProps) => {
  const { code, type, onChange } = props;

  const [confirmEmail] = useConfirmEmailMutation();
  const [sendConfirmEmail] = useSendConfirmEmailMutation();

  const { t } = useTranslation();
  const { errorMessage, fields, legend, submit } = t(
    "features.account.AccountSignUpForm.steps.confirmEmail",
  ) as unknown as {
    errorMessage: Record<HttpStatus, string> & {
      code: Record<AccountStatus | "UNDEFINED", string>;
      email: Record<AccountStatus | "UNDEFINED", string>;
    };
    fields: {
      secret: {
        description: string;
        label: string;
        messages: { invalid: string; required: string };
      };
    };
    legend: string;
    submit: string;
  };

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: { secret: "" },
    resolver: yupResolver(
      yup.object().shape({
        secret: yup
          .string()
          .required(fields.secret.messages.required)
          .length(6, fields.secret.messages.invalid),
      }),
    ),
  });

  return (
    <Form
      actions={
        <Button
          dataLayer={{
            eventAction: "clic",
            eventCategory: "pre-home",
            eventLabel: "confirmation_mail",
          }}
          fluid
          isSubmitting={isSubmitting}
          type="submit"
          variant="action"
        >
          {submit}
        </Button>
      }
      errorMessage={
        errors.root?.server.message && (
          <AccountSignUpFormMarkdown onChange={onChange}>
            {errors.root.server.message}
          </AccountSignUpFormMarkdown>
        )
      }
      name="ConfirmEmailStep"
      onSubmit={handleSubmit(async (values) => {
        try {
          const account = await confirmEmail({
            ...values,
            code,
            type,
          }).unwrap();
          if (account) {
            if (account.status === AccountStatus.INACTIVE) {
              onChange({ step: SignUpStateType.CONFIRM_ACCOUNT, account });
            } else {
              onChange({
                step: SignUpStateType.RESULT,
                message: errorMessage[type][account.status],
                session: { email: account.email },
              });
            }
          } else {
            onChange({
              step: SignUpStateType.RESULT,
              message: errorMessage[type].UNDEFINED,
              session: {},
            });
          }
        } catch (_error) {
          const error = _error as { status: keyof typeof errorMessage };
          if (
            error.status === HttpStatus.BAD_REQUEST ||
            error.status === HttpStatus.MISDIRECTED_REQUEST ||
            error.status === HttpStatus.NOT_FOUND
          ) {
            setError("secret", { message: errorMessage[error.status] });
          } else {
            setError("root.server", {
              message: errorMessage[HttpStatus.INTERNAL_SERVER_ERROR],
            });
          }
        }
      })}
    >
      <Form.Fieldset>
        <Form.Legend>{legend}</Form.Legend>
        <FieldController
          control={control}
          name="secret"
          render={(controllerProps) => (
            <TextField
              {...controllerProps.field}
              description={
                <Markdown
                  options={{
                    overrides: {
                      SendConfirmEmailButton: {
                        component: "button",
                        props: {
                          type: "button",
                          onClick: () => sendConfirmEmail({ code, type }),
                        },
                      },
                    },
                  }}
                >
                  {fields.secret.description}
                </Markdown>
              }
              errorMessage={
                controllerProps.field.errorMessage && (
                  <AccountSignUpFormMarkdown onChange={onChange}>
                    {controllerProps.field.errorMessage}
                  </AccountSignUpFormMarkdown>
                )
              }
              isRequired
              label={fields.secret.label}
              type="text"
            />
          )}
        />
      </Form.Fieldset>
    </Form>
  );
};

type ConfirmSignUpStepState = {
  step: SignUpStateType.CONFIRM_SIGN_UP;
  session: { code: string; username: string };
};
type ConfirmSignUpStepProps = {
  session: { code: string; username: string };
  onChange: (state: SignUpState) => void;
};

const ConfirmSignUpStep = (props: ConfirmSignUpStepProps) => {
  const { session, onChange } = props;

  const { error: _error, isSuccess: isSubmitSuccessful } =
    useConfirmSignUpQuery(session);

  const { t } = useTranslation();
  const { errorMessage, loadingMessage, successMessage } = t(
    "features.account.AccountSignUpForm.steps.confirmSignUp",
  ) as unknown as {
    errorMessage: Record<HttpStatus, string>;
    loadingMessage: string;
    successMessage: string;
  };

  React.useEffect(() => {
    if (isSubmitSuccessful) {
      onChange({
        step: SignUpStateType.RESULT,
        message: successMessage,
        session: { username: session.username },
      });
    }
  }, [isSubmitSuccessful, onChange, session.username, successMessage]);

  React.useEffect(() => {
    if (_error) {
      const error = _error as { status: keyof typeof errorMessage };
      onChange({
        step: SignUpStateType.RESULT,
        message:
          errorMessage[error.status] ??
          errorMessage[HttpStatus.INTERNAL_SERVER_ERROR],
        session: { username: session.username },
      });
    }
  }, [_error, errorMessage, onChange, session.username]);

  return (
    <div role="status">
      <Markdown>{loadingMessage}</Markdown>
    </div>
  );
};

type GetAccountWithCharacterCodeStepState = {
  step: SignUpStateType.GET_ACCOUNT_WITH_CHARACTER_CODE;
};

type GetAccountWithCharacterCodeStepProps = {
  length: 6 | 14;
  onChange: (state: SignUpState) => void;
};

const GetAccountWithCharacterCodeStep = (
  props: GetAccountWithCharacterCodeStepProps,
) => {
  const { length, onChange } = props;

  const { t } = useTranslation();
  const { fields, legend, submit } = t(
    "features.account.AccountSignUpForm.steps.getAccountWithCharacterCode",
    { length, context: length },
  ) as unknown as {
    errorMessage: Record<AccountStatus | HttpStatus, string>;
    fields: {
      code: {
        label: string;
        messages: { invalid: string; matches: string; required: string };
      };
    };
    legend: string;
    submit: string;
  };

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: { code: "", length },
    resolver: yupResolver<any, any, any>(
      yup.object().shape({
        code: yup
          .string()
          .required(fields.code.messages.required)
          .when("length", {
            is: 6,
            then: (schema) =>
              schema
                .matches(/^\d+$/, fields.code.messages.matches)
                .length(6, fields.code.messages.invalid),
            otherwise: (schema) =>
              schema.test(
                "length",
                fields.code.messages.invalid,
                (code) => code.replace(/\s+/g, "").length === 14,
              ),
          }),
        length: yup.number(),
      }),
    ),
  });

  const [sendConfirmEmail] = useSendConfirmEmailMutation();

  return (
    <Form
      actions={
        <Button
          dataLayer={{
            eventAction: "clic",
            eventCategory: "pre-home",
            eventLabel: `code-${length}`,
          }}
          fluid
          isSubmitting={isSubmitting}
          type="submit"
          variant="action"
        >
          {submit}
        </Button>
      }
      errorMessage={
        errors.root?.server.message && (
          <AccountSignUpFormMarkdown onChange={onChange}>
            {errors.root.server.message}
          </AccountSignUpFormMarkdown>
        )
      }
      name={`SignUpGetAccountWith${length}CharacterCodeForm`}
      onSubmit={handleSubmit(async (values) => {
        sendConfirmEmail({ code: values.code, type: "code" });
        onChange({
          step: SignUpStateType.CONFIRM_EMAIL,
          code: values.code,
          type: "code",
        });
      })}
    >
      <Form.Fieldset>
        <Form.Legend>{legend}</Form.Legend>
        <FieldController
          control={control}
          name="code"
          render={(controllerProps) => (
            <TextField
              {...controllerProps.field}
              errorMessage={
                controllerProps.field.errorMessage && (
                  <AccountSignUpFormMarkdown onChange={onChange}>
                    {controllerProps.field.errorMessage}
                  </AccountSignUpFormMarkdown>
                )
              }
              isRequired
              label={fields.code.label}
              type="text"
            />
          )}
        />
      </Form.Fieldset>
    </Form>
  );
};

type GetAccountWithEmailStepState = {
  step: SignUpStateType.GET_ACCOUNT_WITH_EMAIL;
};

type GetAccountWithEmailStepProps = {
  onChange: (state: SignUpState) => void;
};

const GetAccountWithEmailStep = (props: GetAccountWithEmailStepProps) => {
  const { onChange } = props;

  const { t } = useTranslation();
  const { fields, legend, submit } = t(
    "features.account.AccountSignUpForm.steps.getAccountWithEmail",
  ) as unknown as {
    errorMessage: Record<AccountStatus | HttpStatus, string>;
    fields: {
      email: { label: string; messages: { invalid: string; required: string } };
    };
    legend: string;
    submit: string;
  };

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: { email: "" },
    resolver: yupResolver(
      yup.object().shape({
        email: yup
          .string()
          .required(fields.email.messages.required)
          .email(fields.email.messages.invalid),
      }),
    ),
  });

  const [sendConfirmEmail] = useSendConfirmEmailMutation();

  return (
    <>
      <Form
        actions={
          <Button
            dataLayer={{
              eventAction: "clic",
              eventCategory: "pre-home",
              eventLabel: "mail",
            }}
            fluid
            isSubmitting={isSubmitting}
            type="submit"
            variant="action"
          >
            {submit}
          </Button>
        }
        errorMessage={
          errors.root?.server.message && (
            <AccountSignUpFormMarkdown onChange={onChange}>
              {errors.root.server.message}
            </AccountSignUpFormMarkdown>
          )
        }
        name="GetAccountWithEmailStep"
        onSubmit={handleSubmit(async (values) => {
          sendConfirmEmail({ code: values.email, type: "email" });
          onChange({
            step: SignUpStateType.CONFIRM_EMAIL,
            code: values.email,
            type: "email",
          });
        })}
      >
        <Form.Fieldset>
          <Form.Legend>{legend}</Form.Legend>
          <FieldController
            control={control}
            name="email"
            render={(controllerProps) => (
              <TextField
                {...controllerProps.field}
                autoComplete="username"
                errorMessage={
                  controllerProps.field.errorMessage && (
                    <AccountSignUpFormMarkdown onChange={onChange}>
                      {controllerProps.field.errorMessage}
                    </AccountSignUpFormMarkdown>
                  )
                }
                isRequired
                label={fields.email.label}
                type="email"
              />
            )}
          />
        </Form.Fieldset>
      </Form>
      <AccountSignUpFormMessage onChange={onChange} />
    </>
  );
};

type ResultStepState = {
  step: SignUpStateType.RESULT;
  message: string;
  session: { email?: string; username?: string };
};

type ResultStepProps = {
  children: string;
  session: { email?: string; username?: string };
  onChange: (state: SignUpState) => void;
};

const ResultStep = (props: ResultStepProps) => {
  const { children, session, onChange } = props;

  const { t } = useTranslation();
  const { cancel } = t(
    "features.account.AccountSignUpForm.steps.result",
  ) as unknown as {
    cancel: { text: string };
  };

  return (
    <div role="alert">
      <AccountSignUpFormMarkdown session={session} onChange={onChange}>
        {children}
      </AccountSignUpFormMarkdown>
      <Button
        asChild
        dataLayer={{
          eventAction: "clic",
          eventCategory: "pre-home",
          eventLabel: "connexion",
        }}
        fluid={true}
        variant="action"
      >
        <Link to={router.getTo("home")}>{cancel.text}</Link>
      </Button>
    </div>
  );
};
